<script setup lang="ts">
import { ref, inject, getCurrentInstance, onMounted } from "vue";
import { itemCodeToUrlSlug } from "../helpers/url.helper";
import { TranslationService } from "../services/translation.service";
import { CartService } from "../services/cart.service";
import { OverlaysService } from "../services/overlays.service";
import { EOverlay } from "../EOverlay";
import { ItemService } from "../services/item.service";
import { ProductsService } from "../services/products.service";
import { vLoad } from "../directives/v-load";
import { IAddressBlock } from "../interfaces/cart.interfaces";

import Checkout from "./checkout.vue";
import { BASEURL } from "@libTs/cms.globals";

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;

const cartSvc = inject<CartService>("cartSvc") as CartService;
const overlaysSvc = inject<OverlaysService>("overlaysSvc") as OverlaysService;
const itemSvc = inject<ItemService>("itemSvc") as ItemService;
const productsSvc = inject<ProductsService>("productsSvc") as ProductsService;

const { priceDisclaimer } = defineProps({
    priceDisclaimer: {
        type: String,
        required: true,
    },
});

const THP_ITEMCODE = "thp";
const THP_ITEMNAME = "Third-party";

const isLoading = ref(cartSvc.isLoading);
const cartObj = ref(cartSvc.cartObj);
const hasCartInitError = ref(cartSvc.hasCartInitError);

const hideBrandimage: boolean[] = [];
const baseUrl = BASEURL;

// onmounted
onMounted(() => {
    // listen to event
    document.addEventListener("cartLoaded", forceUpdate);
    // console.log("Cart mounted, priceDisclaimer:", priceDisclaimer);

    // console.log("CartObj on mounted:", cartObj.value);
});

const forceUpdate = () => {
    isLoading.value = cartSvc.isLoading;
    cartObj.value = cartSvc.cartObj;
    hasCartInitError.value = cartSvc.hasCartInitError;

    // Force update
    // const instance = getCurrentInstance();
    // instance?.proxy?.$forceUpdate();
};

const availOrLeadText = (item: any) => {
    console.log("Item in availOrLeadText:", item);

    return itemSvc.availOrLeadText(item.Availability, item.LeadTime);
};

const submitOciCart = () => {
    const form = document.getElementById(
        "order-form",
    ) as HTMLFormElement | null;
    const formDataInput = document.getElementById(
        "order-form-data",
    ) as HTMLInputElement | null;
    if (!form || !formDataInput) {
        alert(
            "Submission form for OCI not found.\nPlease report this error to\nsales@brunschwig-ch.com",
        );
        return;
    }

    const orderObj = cartSvc.getOrderRequestObjectOci();
    if (Object.keys(orderObj).length === 0) {
        // console.log("Default cart is empty.");
        return;
    }

    formDataInput.value = JSON.stringify(orderObj);
    // console.log(formDataInput.value);

    // Ask for final confirmation
    // if ( !confirm( 'Do you want to send this basket?' ) ) {
    //   return;
    // }

    // Clear default cart
    cartSvc.clearCart(cartSvc.cartObj.carts[0], true);

    form.submit();
};
</script>

<template>
    <div class="">
        <div v-for="(cart, index) in cartObj.carts">
            <div
                v-if="cartObj.carts.length > 1 && cart.orderReference"
                class="row"
            >
                <h2 class="col-xs-12">
                    Order ref.:
                    <span class="mr-4" v-text="cart.orderReference"></span>
                    <span
                        style="font-weight: normal"
                        v-if="cart.orderName"
                        v-text="cart.orderName"
                    ></span>
                </h2>
            </div>

            <!--  START NEW CART  -->
            <div v-if="cart.items.length > 0">
                <div class="my-4 flex flex-col">
                    <div
                        v-for="(item, index) in cart.items"
                        class="pt-4 md:pt-8 pb-6 md:pb-12 flex border-b border-[#D2D2D1] gap-4 md:gap-12 flex-col md:flex-row"
                    >
                        <!-- Image (Left) -->
                        <div
                            class="w-44 h-24 bg-grey-30 rounded-s flex items-center"
                        >
                            <img
                                v-if="item?.PicSupplier"
                                :src="item?.PicSupplier"
                                :alt="item.ItemName"
                                class="w-full h-full object-contain"
                            />

                            <img
                                v-else
                                :src="baseUrl + 'assets/icons/no-photo.svg'"
                                alt="No Image Available"
                                class="w-full h-1/3 object-contain"
                            />
                        </div>

                        <!-- Details (Center) -->
                        <div class="flex-grow flex flex-col gap-4">
                            <a
                                v-if="item.BrandName"
                                class="flex flex-row items-center gap-4 mb-2"
                                :href="
                                    '/actions/sapShop/products/brand-redirect?brand=' +
                                    item.BrandName
                                "
                            >
                                <div
                                    class="shadow-md p-2"
                                    v-if="!hideBrandimage[index]"
                                >
                                    <img
                                        :src="
                                            '/actions/sapShop/products/brand-image?brand=' +
                                            item.BrandName
                                        "
                                        :alt="item.BrandName"
                                        @error="hideBrandimage[index] = true"
                                        class="h-6 object-contain"
                                    />
                                </div>
                                by {{ item.BrandName }}
                            </a>
                            <a
                                :href="'/product/' + itemCodeToUrlSlug(item.ItemCode)"
                                class="text-xl font-bold"
                                >{{ item.ItemName }}</a
                            >
                            <div class="flex gap-8 text-sm">
                                <div class="flex flex-col">
                                    <span>Art. Nr.</span>
                                    <span class="font-bold">{{
                                        item.ItemCode
                                    }}</span>
                                </div>
                                <div class="flex flex-col">
                                    <span>Packsize</span>
                                    <span class="font-bold">{{
                                        item.Packsize
                                    }}</span>
                                </div>
                                <div class="flex flex-col">
                                    <span>stock / lead time</span>
                                    <span class="font-bold">
                                        {{ availOrLeadText(item) }}
                                    </span>
                                    <!-- TODO: Check if lead time is the correct var here -->
                                </div>
                            </div>
                        </div>

                        <!-- Buttons/Totals (Right) -->
                        <div
                            class="flex flex-col justify-between items-end md:gap-0 gap-4"
                        >
                            <div>
                                <!-- empty div for flexing -->
                            </div>
                            <div class="flex gap-4">
                                <div class="flex flex-row items-center gap-1">
                                    <button
                                        class="w-8 h-8 bg-grey-50 hover:bg-grey-100 rounded-s font-bold text-f6"
                                        @click="
                                            cartSvc.removeFromCart(
                                                item.ItemCode,
                                                1,
                                                cart,
                                            )
                                        "
                                        type="button"
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        v-model="item.quantity"
                                        class="w-12 text-center border-none text-f6 font-bold quantity-input"
                                        min="1"
                                    />
                                    <button
                                        class="w-8 h-8 bg-grey-50 hover:bg-grey-100 rounded-s font-bold text-f6"
                                        @click="
                                            cartSvc.addItemToCart(
                                                item,
                                                1,
                                                cart,
                                                false,
                                            )
                                        "
                                        type="button"
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    class="w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-[#FFE0E0] rounded transition-colors"
                                    @click="
                                        cartSvc.removeFromCart(
                                            item.ItemCode,
                                            item.quantity,
                                            cart,
                                        )
                                    "
                                    type="button"
                                >
                                    <img
                                        :src="`assets/icons/trash.svg`"
                                        alt="trash"
                                    />
                                </button>
                            </div>
                            <div class="font-xl font-bold">
                                CHF {{ item.Preis.toFixed(2) }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="" v-if="cart.items.length <= 0">
                    <span>{{ t("EMPTY_BUCKET") }}</span>
                </div>

                <div
                    v-html="priceDisclaimer"
                    class="cart-redactor-content"
                ></div>
                <div class="flex justify-between text-2xl font-bold pt-8">
                    <span>{{ t("TOTAL_PRICE_NO_VAT") }}</span>
                    <span>{{
                        (Math.round(cart.totalPrice * 100) / 100).toFixed(2)
                    }}</span>
                </div>
            </div>
        </div>
    </div>
</template>
